// Option lists for `multi-select-options` attributes.
//
// These used to live privately inside MultiSelectOptions.tsx. They were lifted
// here for ATAD-134: audienceBreadth.ts needs the same lists to tell whether a
// user has selected every available option, and two copies of the fallback
// logic would drift silently — an attribute would quietly stop being
// detectable with nothing failing.

import revenueRanges from '../assets/data-dictionary/multi-select-options-special-cases/revenueRanges.json';
import employeeRangesOnSite from '../assets/data-dictionary/multi-select-options-special-cases/employeeRangesOnSite.json';
import employeeTotalRanges from '../assets/data-dictionary/multi-select-options-special-cases/employeeTotalRanges.json';
import sicCodes from '../assets/data-dictionary/multi-select-options-special-cases/sicCodes.json';
import sic4Codes from '../assets/data-dictionary/multi-select-options-special-cases/sic4Codes.json';
import naicsCodes from '../assets/data-dictionary/multi-select-options-special-cases/naicsCodes.json';
import tickerSymbols from '../assets/data-dictionary/multi-select-options-special-cases/tickerSymbols.json';
import businessOwner from '../assets/data-dictionary/multi-select-options-special-cases/businessOwner.json';
import contentOrientation from '../assets/data-dictionary/multi-select-options-special-cases/contentOrientation.json';
import purchaseOrientation from '../assets/data-dictionary/multi-select-options-special-cases/purchaseOrientation.json';
import contactOrientation from '../assets/data-dictionary/multi-select-options-special-cases/contactOrientation.json';
import bcTitleLevel from '../assets/data-dictionary/multi-select-options-special-cases/bcTitleLevel.json';

export type SelectOption = { value: string; label: string };

const optionsMap: Record<string, SelectOption[]> = {
  revenueRanges,
  employeeRangesOnSite,
  employeeTotalRanges,
  sicCodes,
  sic4Codes,
  naicsCodes,
  tickerSymbols,
  businessOwner,
  contentOrientation,
  purchaseOrientation,
  contactOrientation,
  bcTitleLevel,
};

export const DEFAULT_OPTIONS_KEY = 'revenueRanges';

/**
 * Resolve an attribute's `optionsKey` to its option list.
 *
 * BOTH fallbacks are load-bearing and must stay in lockstep with the renderer:
 *
 *   - A MISSING key falls back to revenueRanges. "Annual Revenue"
 *     (B2B_Annual_Rev_Range) is the only multi-select-options attribute in the
 *     dictionary with no `optionsKey` at all, and it relies on this branch.
 *   - An UNRECOGNISED key falls back the same way, so a typo in the dictionary
 *     degrades to a wrong-but-rendering picker rather than a crash.
 */
export function getOptionsForKey(optionsKey?: string): SelectOption[] {
  const key = optionsKey || DEFAULT_OPTIONS_KEY;
  return optionsMap[key] ?? revenueRanges;
}

export default getOptionsForKey;
